import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useThumbnailManualReady } from '@elfsight/configurator-preview';
import { Deactivation2 as Deactivation } from '@elfsight/widget-deactivation';
import { parseWidth, useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import Bowser from 'bowser';
import { meta } from '../meta';
import {
  FreeLink,
  FullscreenProvider,
  Layout,
  Pair,
  ProportionalContainer
} from './components';

export function Widget() {
  const {
    displayDeactivation,
    deactivationURL,
    headingText,
    pairs,
    layout: layoutType,
    widgetWidth,
    imageRatio,
    direction,
    dividerInitialPosition,
    dividerSize,
    dividerArrowsEnabled,
    dividerResetOnMouseOut,
    labelPositionHorizontal,
    labelPositionVertical,
    fullscreenEnabled,
    fullscreenPopupWidth,
    interactionBehaviour,
    backgroundType,
    backgroundColor,
    backgroundImage,
    backgroundImageOverlayColor,
    headingColor,
    headingFontSize,
    headingFontWeight,
    pairLabelBackgroundColor,
    pairLabelFontSize,
    pairCaptionFontSize,
    pairCaptionColor,
    dividerColor,
    carouselControlsColor,
    renderThumbnailMode
  } = useSettings();

  const labelPosition =
    direction === 'vertical' ? labelPositionVertical : labelPositionHorizontal;

  const backgroundImageUrl = backgroundImage?.url;
  const withBackgroundColor = backgroundType === 'color' && !!backgroundColor;
  const withBackgroundImage =
    backgroundType === 'image' && !!backgroundImageUrl;

  const withBackground = withBackgroundColor || withBackgroundImage;

  const [device, setDevice] = useState(false);
  const { type: deviceType, agent: deviceAgent } = device;

  const isDeviceTypeMobile = ['mobile', 'tablet'].includes(deviceType);

  const innerRef = useRef(null);

  const [breakpoints, containerRef] = useBreakpoints(500);

  const isMobile = breakpoints[500];

  const [enableThumbnailReadyListener, resolveThumbnailReadyListener] =
    useThumbnailManualReady(renderThumbnailMode);
  const [loadedCount, setLoadedCount] = useState(0);

  let containerPadding;

  if (withBackground) {
    containerPadding = isMobile ? 10 : 24;
  }

  useLayoutEffect(() => {
    enableThumbnailReadyListener();

    const { userAgent } = window.navigator;
    const browser = Bowser.getParser(userAgent);

    setDevice({
      type: browser.getPlatformType(true),
      agent: browser.getBrowserName()
    });
  }, []);

  useEffect(() => {
    const loadedAllCarousel = layoutType === 'carousel' && loadedCount;
    const loadedAllList = layoutType === 'list' && pairs.length === loadedCount;
    if (loadedAllCarousel || loadedAllList) {
      resolveThumbnailReadyListener();
    }
  }, [
    layoutType,
    loadedCount,
    pairs.length,
    renderThumbnailMode,
    resolveThumbnailReadyListener
  ]);

  const pairProps = {
    dividerColor,
    direction,
    dividerInitialPosition,
    dividerSize,
    dividerArrowsEnabled,
    dividerResetOnMouseOut,
    labelPosition,
    interactionBehaviour,
    fullscreenEnabled,
    compact: isMobile,
    labelBackgroundColor: pairLabelBackgroundColor,
    labelFontSize: pairLabelFontSize,
    captionFontSize: pairCaptionFontSize,
    captionColor: pairCaptionColor,
    withCaptionMagicMargins: layoutType === 'carousel',
    isMobile: isDeviceTypeMobile,
    isSafari: deviceAgent === 'Safari'
  };

  const renderInnerChildren = (width, height) => (
    <>
      {!!headingText && (
        <Heading
          _elementPaddingBottom={isMobile ? 12 : 24}
          _elementColor={headingColor}
          _elementFontWeight={headingFontWeight}
          _elementFontSize={headingFontSize}
        >
          {headingText}
        </Heading>
      )}

      {pairs?.length > 0 && (
        <Content>
          <Layout
            viewWidth={width}
            viewHeight={height}
            type={layoutType}
            compact={isMobile}
            controlsColor={carouselControlsColor}
          >
            {pairs.map((data) => (
              <Pair
                key={data.id}
                {...pairProps}
                viewWidth={width}
                viewHeight={height}
                data={data}
                onLoaded={() => {
                  const isThumbnailModeDisabled =
                    !renderThumbnailMode ||
                    (layoutType === 'carousel' && index);
                  if (isThumbnailModeDisabled) {
                    return;
                  }

                  setLoadedCount((count) => count + 1);
                }}
              />
            ))}
          </Layout>
        </Content>
      )}

      <FreeLink />
    </>
  );

  return (
    <FullscreenProvider
      enabled={fullscreenEnabled}
      popupWidth={fullscreenPopupWidth}
      imageRatio={imageRatio}
      isMobile={isMobile}
      pairs={pairs}
      pairProps={pairProps}
    >
      <Deactivation
        appAlias={meta.alias}
        displayDeactivation={displayDeactivation}
        deactivationURL={deactivationURL}
        childrenRef={containerRef}
      >
        <Container
          _elementPadding={containerPadding}
          _elementBackgroundColor={
            withBackgroundColor ? backgroundColor : undefined
          }
        >
          {withBackgroundImage && (
            <Backdrop
              _elementBackgroundImage={backgroundImageUrl}
              _overlayColor={backgroundImageOverlayColor}
            />
          )}

          <Inner ref={innerRef} _elementMaxWidth={parseWidth(widgetWidth)}>
            <ProportionalContainer ref={innerRef} ratio={imageRatio}>
              {renderInnerChildren}
            </ProportionalContainer>
          </Inner>
        </Container>
      </Deactivation>
    </FullscreenProvider>
  );
}

const Container = styled.div<{
  _elementPadding?: number;
  _elementBackgroundColor?: string;
}>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  width: 100%;

  ${({ _elementPadding: padding, _elementBackgroundColor: backgroundColor }) =>
    css({
      padding,
      backgroundColor
    })};

  & div:empty {
    display: block;
  }
`;

const Backdrop = styled.div<{
  _elementBackgroundImage: string;
  _overlayColor: string | null;
}>`
  position: absolute;
  z-index: 1;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-image: ${({ _elementBackgroundImage }) =>
    _elementBackgroundImage ? `url(${_elementBackgroundImage})` : 'none'};

  ${({ _overlayColor }) =>
    _overlayColor &&
    css`
      &::after {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: ${_overlayColor};
        content: '';
      }
    `};
`;

const Inner = styled.div<{
  _elementMaxWidth: string;
}>`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${({ _elementMaxWidth }) => _elementMaxWidth};
`;

const Heading = styled.div<{
  _elementPaddingBottom: number;
  _elementFontWeight: number;
  _elementFontSize: number;
  _elementColor: string;
}>`
  text-align: center;
  line-height: 1.2;

  ${({
    _elementPaddingBottom,
    _elementFontWeight,
    _elementFontSize,
    _elementColor
  }) =>
    css({
      paddingBottom: _elementPaddingBottom,
      fontWeight: _elementFontWeight,
      fontSize: _elementFontSize,
      color: _elementColor
    })};
`;

const Content = styled.div``;
